'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shadcn/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { ISummerSale } from '@/sanity/lib/definitions';
import { DEFAULT_LANGUAGE, resolveI18nText } from '@/sanity/globals/I18n';
import { cn } from '@/lib/utils';
import { resolveLink } from '@/sanity/globals/CTA';
import popupHero from '@/public/global/summer-sale/popup-hero.png';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  expired: boolean;
}

// Countdown unit labels per language (standard time units — kept in code).
const LABELS: Record<string, [string, string, string, string]> = {
  en: ['DAYS', 'HRS', 'MIN', 'SEC'],
  de: ['TAGE', 'STD', 'MIN', 'SEK'],
  br: ['DIAS', 'HRS', 'MIN', 'SEG'],
};

function getTimeLeft(endDate: Date): TimeLeft {
  const diff = endDate.getTime() - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  const totalSeconds = Math.floor(diff / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
    expired: false,
  };
}

function pad(n: number) {
  return String(n).padStart(2, '0');
}

interface SummerSalePopUpProps {
  data: ISummerSale;
  ctaUrl: string;
  language?: string;
}

export default function SummerSalePopup({
  data,
  ctaUrl,
  language,
}: SummerSalePopUpProps) {
  const [open, setOpen] = useState(false);
  const endDate = useMemo(
    () => (data.endDate ? new Date(data.endDate) : null),
    [data.endDate],
  );
  const link = resolveLink(ctaUrl, language || DEFAULT_LANGUAGE);
  const [finalLink, setFinalLink] = useState("");
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);

  useEffect(() => {
    if (!link) return;
    setFinalLink(link);
  }, [link]);

  useEffect(() => {
    if (!endDate) return;
    setTimeLeft(getTimeLeft(endDate));
    const interval = setInterval(() => setTimeLeft(getTimeLeft(endDate)), 1000);
    return () => clearInterval(interval);
  }, [endDate]);

  // Open once the user scrolls past ~85% of the first viewport (matches Birthday popup).
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > window.innerHeight * 0.85) {
        setOpen(true);
        window.removeEventListener('scroll', handleScroll);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (endDate && timeLeft?.expired) return null;

  const labels = LABELS[language ?? 'en'] ?? LABELS.en;
  const offer = resolveI18nText(data.popupText, data.popupTextI18n, language);
  const subtext = resolveI18nText(data.popupSubtext, data.popupSubtextI18n, language);
  const ctaLabel =
    resolveI18nText(data.popupCtaLabel, data.popupCtaLabelI18n, language) ||
    resolveI18nText(data.bannerCtaLabel, data.bannerCtaLabelI18n, language);

  const units = timeLeft
    ? [
        { value: timeLeft.days, label: labels[0] },
        { value: timeLeft.hours, label: labels[1] },
        { value: timeLeft.minutes, label: labels[2] },
        { value: timeLeft.seconds, label: labels[3] },
      ]
    : [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="!w-[min(43rem,calc(100%-1.5rem))] md:!w-[min(52rem,calc(100%-3rem))] max-h-[95dvh] overflow-y-auto overflow-x-clip !border-0 !bg-white !p-0 !duration-700 rounded-[2rem] md:rounded-[2.75rem]">
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>Summer Sale</DialogTitle>
          </DialogHeader>
        </VisuallyHidden>

        <div className="flex flex-col">
          <Image src={popupHero} alt="" className="!h-auto !w-full" priority />

          <div className="flex flex-col items-center gap-[1.75rem] px-[2.5rem] pt-[2.5rem] pb-[3.5rem] md:gap-[2.25rem] md:px-[5rem] md:pt-[3.5rem] md:pb-[4.5rem]">
            <p className="m-0 text-center font-bebas uppercase leading-[0.8] tracking-[-0.3rem] md:tracking-[-0.45rem] text-[6rem] md:text-[9rem]" style={{ color: '#ff7248' }}>
              Summer Sale
            </p>

            {(offer || subtext) && (
              <div className="flex flex-col gap-[0.25rem] text-center">
                {offer && (
                  <p className="m-0 font-geist font-bold text-[1.6rem] md:text-[2.1rem] leading-[1.29]" style={{ color: '#3c3c3c' }}>
                    {offer}
                  </p>
                )}
                {subtext && (
                  <p className="m-0 font-geist text-[1.5rem] md:text-[1.9rem] leading-[1.29]" style={{ color: '#3c3c3c' }}>
                    {subtext}
                  </p>
                )}
              </div>
            )}

            {endDate && timeLeft && (
              <div className="flex items-start gap-[0.6rem]">
                {units.map((unit, index) => (
                  <div key={unit.label} className="flex items-start gap-[0.6rem]">
                    <div className="flex flex-col items-center">
                      <span className="font-bebas leading-[0.9] text-[3.75rem] md:text-[4.75rem]" style={{ color: '#262626' }}>
                        {pad(unit.value)}
                      </span>
                      <span className="font-geist font-medium text-[0.9rem] md:text-[1.05rem] tracking-[0.18rem]" style={{ color: '#666' }}>
                        {unit.label}
                      </span>
                    </div>
                    {index < units.length - 1 && (
                      <span className="font-bebas leading-[0.9] text-[3.75rem] md:text-[4.75rem]" style={{ color: '#262626' }}>
                        :
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {ctaLabel && (
              <Link
                href={finalLink ?? ctaUrl}
                className={cn(
                  'mt-[0.5rem] inline-flex items-center justify-center gap-[0.75rem] bg-black px-[5rem] py-[1.75rem] md:px-[6rem] md:py-[2.1rem] font-geist font-bold text-white text-[1.5rem] md:text-[1.95rem] tracking-[-0.05rem] transition-opacity hover:opacity-90',
                )}
              >
                {ctaLabel}
                <span aria-hidden="true">→</span>
              </Link>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
